import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useVerifyIsMobile } from 'hooks/useVerifyIsMobile';
import { useSelector } from 'react-redux';
import { LandingHeader } from './components/LandingHeader';
import { LandingMainMetadata } from './components/LandingMainMetadata';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getRegion } from 'utils/Region';
import { selectMetadataKeyByRegion } from 'reducers/selectors/metadata';
import FadeInImage from 'components/FadeInImage';
import { useTranslation } from 'hooks/useTranslation';
import { TRANSLATION_KEYS } from './constants';
import { useMetrics } from 'hooks/metrics/useMetrics';
import { AppAuthConfirmModal } from 'components/AppAuthConfirmModal';
import LandingSlider from './components/LandingSlider';
import { useLandingCarousel } from './hooks/useLandingCarousel';
import { FadeBackground } from 'components/FadeBackground';
import ContentLoader from 'containers/Player/components/ContentLoader';
import './style.scss';

const ROUTE_PATHS = {
  login: {
    app: 'app-login',
    web: 'login',
  },
  register: {
    app: 'app-register',
    web: 'register',
  },
};

const INTERACTION_TYPES = {
  login: 'inicia sesion',
  register: 'registrate con tu correo',
};

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userhash, setUserhash] = useState(false);
  const [userData, setUserData] = useState({});
  const [searchParams] = useSearchParams();
  const { isMobile } = useVerifyIsMobile(768);
  const { translationMapper } = useTranslation();
  const { buildMetricsEvent, sendMetricsEvent } = useMetrics();
  const { carouselData, isMediaLoading, showCarousel } = useLandingCarousel();
  const telmexIframeConf = useSelector(
    selectMetadataKeyByRegion('iframe_telmex_config'),
  );
  const { Landing_Imagen_Background, Landing_Imagen_Poster1 } = useSelector(
    state => state.asset,
  );
  const [imgBackground, setImgBackground] = useState('');
  const isAppSource = useMemo(() => searchParams.get('source') === 'app', [
    searchParams,
  ]);
  const translatedTexts = useMemo(
    () => translationMapper(TRANSLATION_KEYS),
    [],
  );

  const handleRedirect = (type = 'login') => {
    const sourceType = isAppSource ? 'app' : 'web';
    const path = ROUTE_PATHS[type][sourceType];
    const interactionType = INTERACTION_TYPES[type];

    const event = buildMetricsEvent('landing', {
      content_section: 'landing',
      interaction_type: interactionType,
      modulo_name: 'landing',
      method: 'mi telmex',
    });

    sendMetricsEvent(event);
    navigate(`/${getRegion()}/${path}${location.search}`);
  };

  useEffect(() => {
    if (location.pathname.includes('onboarding') && !isAppSource) {
      navigate(`/${getRegion()}/landing${location.search}`, {
        replace: true,
      });
    }
  }, []);

  if (userhash) {
    return (
      <AppAuthConfirmModal
        isMobileAuth={isAppSource}
        userhash={userhash}
        userData={userData}
      />
    );
  }

  if (isMediaLoading) return <ContentLoader />;

  return (
    <article className="LandingPageV2">
      {!showCarousel ? (
        <img
          className="LandingPageV2__Background"
          src={Landing_Imagen_Background}
          alt="landing background"
        />
      ) : (
        <FadeBackground src={imgBackground} />
      )}
      <LandingHeader
        isMobile={isMobile}
        texts={translatedTexts}
        onRedirect={handleRedirect}
      />
      <div className="LandingPageV2__Main">
        <LandingMainMetadata
          isMobile={isMobile}
          texts={translatedTexts}
          onRedirect={handleRedirect}
          showTelmex={telmexIframeConf?.enable}
          setUserhash={setUserhash}
          setUserData={setUserData}
        />
        {showCarousel ? (
          <LandingSlider
            setImgBackground={setImgBackground}
            items={carouselData}
          />
        ) : (
          <FadeInImage
            imageAlt="landing-poster"
            imageSrc={Landing_Imagen_Poster1}
            classNames={['LandingPageV2__Main__Img']}
            width={isMobile ? 208 : 361}
          />
        )}
      </div>
    </article>
  );
};

export default LandingPage;
